import { useMutation, useQuery } from "@apollo/client";
import { gql } from "@/gql";
import { useSalonStore } from "@/lib/store/salon";

// GraphQL query for getting notification settings
const GET_NOTIFICATION_SETTINGS = gql(`
  query GetNotificationSettings($salonId: ID!) {
    getNotificationSettings(salonId: $salonId) {
      usage {
        EMAIL {
          currentUsage
          monthlyLimit
          remainingQuota
          isUnlimited
          usagePercentage
        }
        SMS {
          currentUsage
          monthlyLimit
          remainingQuota
          isUnlimited
          usagePercentage
        }
        WHATSAPP {
          currentUsage
          monthlyLimit
          remainingQuota
          isUnlimited
          usagePercentage
        }
        resetDate
      }
      chainSettings {
        emailEnabled
        phoneProviders {
          type
          enabled
          order
          draggable
        }
      }
    }
  }
`);

// GraphQL query for getting multi-notification settings
const GET_MULTI_NOTIFICATION_SETTINGS = gql(`
  query GetMultiNotificationSettings($salonId: ID!) {
    getMultiNotificationSettings(salonId: $salonId) {
      usage {
        EMAIL {
          currentUsage
          monthlyLimit
          remainingQuota
          isUnlimited
          usagePercentage
        }
        SMS {
          currentUsage
          monthlyLimit
          remainingQuota
          isUnlimited
          usagePercentage
        }
        WHATSAPP {
          currentUsage
          monthlyLimit
          remainingQuota
          isUnlimited
          usagePercentage
        }
        resetDate
      }
      notificationTypes {
        type
        displayName
        description
        chainConfigs {
          chainKey
          displayName
          description
          chainSettings {
            emailEnabled
            phoneProviders {
              type
              enabled
              order
              draggable
            }
          }
        }
      }
    }
  }
`);

// GraphQL mutation for updating notification settings
const UPDATE_NOTIFICATION_SETTINGS = gql(`
  mutation UpdateNotificationSettings($salonId: ID!, $input: UpdateNotificationSettingsInput!) {
    updateNotificationSettings(salonId: $salonId, input: $input)
  }
`);

// GraphQL mutation for updating multi-notification settings
const UPDATE_MULTI_NOTIFICATION_SETTINGS = gql(`
  mutation UpdateMultiNotificationSettings($salonId: ID!, $input: UpdateMultiNotificationSettingsInput!) {
    updateMultiNotificationSettings(salonId: $salonId, input: $input)
  }
`);

export interface ChannelUsageStats {
  currentUsage: number;
  monthlyLimit: number;
  remainingQuota: number;
  isUnlimited: boolean;
  usagePercentage: number;
}

export interface NotificationUsageStats {
  EMAIL: ChannelUsageStats;
  SMS: ChannelUsageStats;
  WHATSAPP: ChannelUsageStats;
  resetDate: string;
}

export interface PhoneProviderSetting {
  type: string;
  enabled: boolean;
  order: number;
  draggable: boolean;
}

export interface NotificationChainSettings {
  emailEnabled: boolean;
  phoneProviders: PhoneProviderSetting[];
}

export interface NotificationSettings {
  usage: NotificationUsageStats;
  chainSettings: NotificationChainSettings;
}

export interface UpdateNotificationSettingsInput {
  emailEnabled: boolean;
  phoneProviders: Array<{
    type: string;
    enabled: boolean;
    order: number;
  }>;
}

// New types for multi-notification settings
export enum NotificationType {
  CUSTOMER = "CUSTOMER",
  USER = "USER",
}

export interface ChainSettings {
  emailEnabled: boolean;
  phoneProviders: PhoneProviderSetting[];
}

export interface NotificationChainConfig {
  chainKey: string;
  displayName: string;
  description: string;
  chainSettings: ChainSettings;
}

export interface NotificationTypeSettings {
  type: NotificationType;
  displayName: string;
  description: string;
  chainConfigs: NotificationChainConfig[];
}

export interface MultiNotificationSettings {
  usage: NotificationUsageStats;
  notificationTypes: NotificationTypeSettings[];
}

export interface UpdateChainSettingsInput {
  emailEnabled: boolean;
  phoneProviders: Array<{
    type: string;
    enabled: boolean;
    order: number;
  }>;
}

export interface UpdateNotificationChainInput {
  chainKey: string;
  chainSettings: UpdateChainSettingsInput;
}

export interface UpdateMultiNotificationSettingsInput {
  notificationType: NotificationType;
  chainUpdates: UpdateNotificationChainInput[];
}

export function useNotificationSettings() {
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id;

  return useQuery(GET_NOTIFICATION_SETTINGS, {
    variables: { salonId: salonId ?? "" },
    skip: !salonId,
  });
}

export function useUpdateNotificationSettings() {
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id;

  return useMutation(UPDATE_NOTIFICATION_SETTINGS, {
    refetchQueries: salonId
      ? [
          {
            query: GET_NOTIFICATION_SETTINGS,
            variables: { salonId },
          },
        ]
      : [],
  });
}

// New hooks for multi-notification settings
export function useMultiNotificationSettings() {
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id;

  return useQuery(GET_MULTI_NOTIFICATION_SETTINGS, {
    variables: { salonId: salonId ?? "" },
    skip: !salonId,
  });
}

export function useUpdateMultiNotificationSettings() {
  const { currentSalon } = useSalonStore();
  const salonId = currentSalon?.id;

  return useMutation(UPDATE_MULTI_NOTIFICATION_SETTINGS, {
    refetchQueries: salonId
      ? [
          {
            query: GET_MULTI_NOTIFICATION_SETTINGS,
            variables: { salonId },
          },
        ]
      : [],
  });
}
